// React
import { useEffect, useState } from "react";
import clsx from "clsx";

// MUI
import { Divider, Grid, Stack, Tab, Tabs, Typography } from "@mui/material";

// Icons
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";

// Components
import CustomizedSwitches from "@/Layouts/components/InputComponents/CustomizedSwitches";
import TabsPanels from "./TabsPanels";
import PropTypes from "prop-types";

const evaluateError = (index, errors = null) => {
	if (errors?.length) {
		return Boolean(errors?.[index]);
	}
	return false;
};

export default function TabSelector(props) {
	const { renderComponent: RenderComponent } = props;
	const [value, setValue] = useState(Number(0));
	const [content, setContent] = useState([]);
	const [tabs, setTabs] = useState([]);
	const [topSw, setTopSwitch] = useState(false);
	const [topSwitchAdditionalChecked, setTopSwitchAdditionalChecked] = useState(false);
	const [bottomSw, setBottomSwitch] = useState(false);

	const handleTopSwitch = (newValue) => {
		setTopSwitch(newValue);
		props.topSwCallback(newValue);
	};

	const handleTopSwitchAdditional = (value) => {
		setTopSwitch(false);
		setTopSwitchAdditionalChecked(value);
		if (props.onTopSwitchAdditionalChange) {
			props.onTopSwitchAdditionalChange(value);
		}
	};

	const handleBottomSwitch = (newValue) => {
		setBottomSwitch(newValue);
		props.bottomSwCallback(newValue);
	};

	const handleChange = (event, newValue) => {
		if (props.tabValue) {
			props.tabValue(newValue);
		}
		setValue(newValue);
		setContent(props.content);
	};

	useEffect(() => {
		let isMounted = true;

		if (isMounted) {
			if (props.content?.length) {
				setContent(props.content);
			}

			if (props.data.tabs?.length) {
				setTabs(props.data.tabs);
			}

			if (props.defaultTab) {
				setValue(Number(props.defaultTab));
			}

			setTopSwitch(Boolean(props?.data?.topSwValue));
			setTopSwitchAdditionalChecked(Boolean(props?.data?.topSwitchAdditionalValue));
			setBottomSwitch(Boolean(props?.data?.bottomSwValue));
		}

		return () => {
			isMounted = false;
			setTabs([]);
			setContent([]);
		};
	}, [
		props.content,
		props.data?.bottomSwValue,
		props.data.tabs,
		props.data?.topSwValue,
		props.data?.topSwitchAdditionalValue,
		props.defaultTab,
	]);

	function renderTabComponent() {
		return <RenderComponent {...props.specialProps} value={value} tabs={tabs} />;
	}

	const hasTop = () => props.data.topSwShow && props.data.topSwitchAdditional;

	const hasTopSwitch = () => props.data?.topSwitchAdditionalUncheckedLabel || props.data?.topSwitchAdditionalCheckedLabel;

	return (
		<Grid container direction="column" justifyContent="flex-end">
			<Grid item xs={12} className="!border-disabled !border-b-[1px] !border-solid">
				<Grid container direction="row" justifyContent="space-between" alignItems="flex-end">
					<Grid item>
						{Boolean(tabs.length) && (
							<Tabs
								value={Number(value)}
								onChange={handleChange}
								aria-label="custom-tabs"
								className="custom-tabs"
								{...props.tabsProps}
							>
								{tabs.map((tab, key) => {
									const isDisabled = Boolean(props.data?.disabledTab?.[key]);
									const isError = evaluateError(key, props.errors);
									return (
										<Tab
											key={`${tab}_${key}`}
											className={clsx({
												"!border-disabled !border-b-0": true,
												"opacity-100 !bg-disabled": isDisabled,
											})}
											value={key}
											disabled={isDisabled}
											label={
												<Typography
													className={clsx({
														"!text-alert in-error": isError,
														"!text-white": isDisabled,
													})}
												>
													{tab}
													<ErrorOutlineRoundedIcon
														fontSize={"small"}
														sx={{
															marginLeft: "5px",
															color: "var(--text-alert-color)",
														}}
													/>
												</Typography>
											}
										/>
									);
								})}
							</Tabs>
						)}
					</Grid>
					{props.data.topSwShow || props.data.topSwitchAdditional ? (
						<Grid item sx={{ marginRight: "30px" }}>
							<Stack direction={"row"} spacing={2}>
								{props.data.topSwitchAdditional && (
									<CustomizedSwitches
										disabled={props.readOnly}
										textLeft
										textChecked={props.data?.topSwitchAdditionalCheckedLabel}
										textUnChecked={hasTopSwitch}
										checked={topSwitchAdditionalChecked}
										onChange={handleTopSwitchAdditional}
									/>
								)}

								{Boolean(hasTop()) && (
									<Divider
										className="!border-disabled"
										textAlign="center"
										orientation="vertical"
										style={{ height: "18px", marginLeft: "30px", marginTop: "5px" }}
									/>
								)}

								<CustomizedSwitches
									disabled={props.readOnly || topSwitchAdditionalChecked}
									textLeft
									textChecked={props.data.topSwLabel}
									textUnChecked={props.data.topSwLabel}
									checked={topSw}
									onChange={handleTopSwitch}
								/>
							</Stack>
						</Grid>
					) : (
						""
					)}
				</Grid>
			</Grid>

			{props.data.bottomSwShow && (
				<Grid item xs={12}>
					<Grid container direction="row" justifyContent="flex-end" alignItems="flex-start">
						<Grid item sx={{ marginRight: "30px" }}>
							<CustomizedSwitches
								disabled={props.readOnly}
								textLeft
								textChecked={props.data.bottomSwLabel}
								textUnChecked={props.data.bottomSwLabel}
								checked={bottomSw}
								onChange={handleBottomSwitch}
							/>
						</Grid>
					</Grid>
				</Grid>
			)}

			{props.reRenderOnTabLoad
				? renderTabComponent()
				: Boolean(content?.length) && <TabsPanels content={content} value={value} contentProps={props.contentProps} />}
		</Grid>
	);
}

TabSelector.propTypes = {
	content: PropTypes.array,
	contentProps: PropTypes.object,
	data: PropTypes.object,
	defaultTab: PropTypes.number,
	errors: PropTypes.array,
	onTopSwitchAdditionalChange: PropTypes.func,
	readOnly: PropTypes.bool,
	reRenderOnTabLoad: PropTypes.bool,
	specialProps: PropTypes.object,
	tabValue: PropTypes.func,
	tabsProps: PropTypes.object,
	topSwCallback: PropTypes.func,
	renderComponent: PropTypes.func,
};
